if (!customElements.get('td-marquee')) {
  class TDMarquee extends HTMLElement {
    static SELECTORS = {
      wrapper: '[data-td-marquee-wrapper]',
      content: '[data-td-marquee-content]',
      track: '[data-td-marquee-track]',
      image: '[data-td-marquee-image] img',
    };

    static SIZE_MULTIPLIER = 2;
    static MAX_COPIES = 20;
    static DRAG_THRESHOLD = 6;

    constructor() {
      super();
      this.handleResize = this.debounce(this.rebuildMarquee.bind(this), 200);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleModeChange = this.handleModeChange.bind(this);
      this.handleImageLoad = this.handleImageLoad.bind(this);
    }

    connectedCallback() {
      this.wrapper = this.querySelector(TDMarquee.SELECTORS.wrapper);
      this.content = this.querySelector(TDMarquee.SELECTORS.content);
      this.track = this.querySelector(TDMarquee.SELECTORS.track);
      this.imageNodes = Array.from(this.querySelectorAll(TDMarquee.SELECTORS.image));
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.isDragging = false;
      this.dragController = null;

      if (!this.wrapper || !this.content || !this.track) {
        return;
      }

      window.addEventListener('resize', this.handleResize);
      this.bindMediaQueryListener(true);
      this.bindImageLoadListeners(true);
      this.applyMode();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.bindMediaQueryListener(false);
      this.bindImageLoadListeners(false);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.cleanupDrag();
    }

    bindMediaQueryListener(shouldBind) {
      const method = shouldBind ? 'addEventListener' : 'removeEventListener';
      const legacyMethod = shouldBind ? 'addListener' : 'removeListener';

      if (typeof this.reduceMotionQuery?.[method] === 'function') {
        this.reduceMotionQuery[method]('change', this.handleModeChange);
      } else if (typeof this.reduceMotionQuery?.[legacyMethod] === 'function') {
        this.reduceMotionQuery[legacyMethod](this.handleModeChange);
      }
    }

    bindImageLoadListeners(shouldBind) {
      this.imageNodes.forEach((image) => {
        const method = shouldBind ? 'addEventListener' : 'removeEventListener';
        image[method]('load', this.handleImageLoad);
      });
    }

    handleModeChange() {
      this.applyMode();
    }

    handleImageLoad() {
      if (!this.isStaticMode()) {
        this.rebuildMarquee();
      }
    }

    isStaticMode() {
      return this.isDesignMode || this.reduceMotionQuery.matches;
    }

    applyMode() {
      const isReducedMotion = this.reduceMotionQuery.matches;
      this.toggleAttribute('data-td-reduced-motion', isReducedMotion);
      this.toggleAttribute('data-td-editor-static', this.isDesignMode);

      if (this.isStaticMode()) {
        this.removeEventListener('pointerdown', this.handlePointerDown);
        this.cleanupDrag();
        this.resetTrackCopies();
        this.setAttribute('data-td-ready', 'false');
        return;
      }

      this.addEventListener('pointerdown', this.handlePointerDown);
      requestAnimationFrame(() => this.rebuildMarquee());
    }

    rebuildMarquee() {
      if (!this.wrapper || !this.content || !this.track || this.isStaticMode() || this.isDragging) {
        return;
      }

      this.resetTrackCopies();

      const copies = this.calculateCopies();
      this.addTrackCopies(copies);
      this.duplicateContent();
      this.setDuration(copies);
      this.setAttribute('data-td-ready', 'true');
      this.restartAnimation();
    }

    calculateCopies() {
      const viewportWidth = this.getBoundingClientRect().width;
      const trackWidth = this.track.getBoundingClientRect().width;

      if (viewportWidth <= 0 || trackWidth <= 0) {
        return 1;
      }

      const requiredCopies = Math.ceil((viewportWidth * TDMarquee.SIZE_MULTIPLIER) / trackWidth);
      return Math.max(1, Math.min(requiredCopies, TDMarquee.MAX_COPIES));
    }

    resetTrackCopies() {
      this.wrapper.querySelector('[data-td-marquee-clone]')?.remove();

      Array.from(this.content.children).forEach((child, index) => {
        if (index > 0) {
          child.remove();
        }
      });
    }

    addTrackCopies(copies) {
      for (let index = 1; index < copies; index += 1) {
        const clone = this.track.cloneNode(true);
        clone.removeAttribute('data-td-marquee-track');
        clone.setAttribute('aria-hidden', 'true');
        this.content.appendChild(clone);
      }
    }

    duplicateContent() {
      const clone = this.content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-td-marquee-clone', '');
      this.wrapper.appendChild(clone);
    }

    setDuration(copies) {
      const baseSpeed = parseFloat(getComputedStyle(this).getPropertyValue('--td-marquee-speed')) || 18;
      const duration = Math.max(baseSpeed, Math.sqrt(copies) * baseSpeed);
      this.style.setProperty('--td-marquee-duration', `${duration}s`);
    }

    restartAnimation() {
      const animations = this.wrapper.getAnimations();
      requestAnimationFrame(() => {
        animations.forEach((animation) => {
          animation.currentTime = 0;
        });
      });
    }

    getTranslateX(element) {
      const transform = getComputedStyle(element).transform;

      if (!transform || transform === 'none') {
        return 0;
      }

      const matrix = transform.match(/matrix.*\((.+)\)/);
      return matrix?.[1] ? parseFloat(matrix[1].split(', ')[4]) || 0 : 0;
    }

    getTrackDistance() {
      const style = getComputedStyle(this);
      const gap = parseFloat(style.getPropertyValue('--td-marquee-track-gap')) || 0;
      return (this.content?.offsetWidth || 0) + gap;
    }

    getAnimationDurationMs(animation) {
      const duration = animation?.effect?.getComputedTiming?.().duration;
      return Number.isFinite(duration) ? duration : 0;
    }

    getActiveAnimation() {
      return this.wrapper?.getAnimations()?.[0] || null;
    }

    normalizeProgress(value) {
      return ((value % 1) + 1) % 1;
    }

    handlePointerDown(event) {
      if (event.button !== 0 || this.isDragging || this.isStaticMode()) {
        return;
      }

      this.startX = event.clientX;
      this.startY = event.clientY;
      this.dragController = new AbortController();
      this.dragAnimation = null;
      this.dragDuration = 0;
      this.dragDistance = 0;
      this.dragStartProgress = 0;
      this.dragFallbackX = 0;

      const onPointerMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - this.startX;
        const deltaY = moveEvent.clientY - this.startY;

        if (!this.isDragging && Math.abs(deltaX) > TDMarquee.DRAG_THRESHOLD) {
          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            this.cleanupDrag();
            return;
          }

          this.isDragging = true;
          this.classList.add('is-dragging');
          this.dragAnimation = this.getActiveAnimation();
          this.wrapper.style.animationPlayState = 'paused';

          if (this.dragAnimation) {
            this.dragDuration = this.getAnimationDurationMs(this.dragAnimation);
            this.dragDistance = this.getTrackDistance();

            if (this.dragDuration > 0) {
              const animationTime = Number(this.dragAnimation.currentTime) || 0;
              this.dragStartProgress = this.normalizeProgress(animationTime / this.dragDuration);
            }
          } else {
            this.dragFallbackX = this.getTranslateX(this.wrapper);
          }

          try {
            this.setPointerCapture(moveEvent.pointerId);
          } catch {
            // Ignore capture failures from browsers that deny pointer capture here.
          }
        }

        if (!this.isDragging) {
          return;
        }

        moveEvent.preventDefault();

        if (this.dragAnimation && this.dragDuration > 0 && this.dragDistance > 0) {
          const progress = this.normalizeProgress(this.dragStartProgress - deltaX / this.dragDistance);
          this.dragAnimation.currentTime = progress * this.dragDuration;
          return;
        }

        this.wrapper.style.transform = `translateX(${this.dragFallbackX + deltaX}px)`;
      };

      const onPointerUp = (upEvent) => {
        if (this.isDragging) {
          try {
            this.releasePointerCapture(upEvent.pointerId);
          } catch {
            // Ignore release failures when capture was never granted.
          }
        }

        this.cleanupDrag();
      };

      document.addEventListener('pointermove', onPointerMove, {
        signal: this.dragController.signal,
        passive: false,
      });
      document.addEventListener('pointerup', onPointerUp, { signal: this.dragController.signal });
      document.addEventListener('pointercancel', onPointerUp, { signal: this.dragController.signal });
    }

    cleanupDrag() {
      this.dragController?.abort();
      this.dragController = null;
      this.isDragging = false;
      this.classList.remove('is-dragging');

      if (this.wrapper) {
        this.wrapper.style.transform = '';
        this.wrapper.style.animationPlayState = '';
      }

      this.dragAnimation = null;
      this.dragDuration = 0;
      this.dragDistance = 0;
      this.dragStartProgress = 0;
    }

    debounce(callback, delay) {
      let timeoutId;

      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => callback.apply(this, args), delay);
      };
    }
  }

  customElements.define('td-marquee', TDMarquee);
}
